import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { X } from 'lucide-react'
import { usePlayer } from '../lib/player'
import { LatexText } from '../lib/latex'

export default function LyricsPanel() {
  const { showLyrics, closeLyrics, paper, suno, currentPaperId } = usePlayer()
  const location = useLocation()
  
  // Hide on song pages and about page (they have their own lyrics display or none)
  const isSongPage = location.pathname.startsWith('/virtual/')
  const isAboutPage = location.pathname === '/about'
  
  // Close on escape key (mobile only)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showLyrics) {
        closeLyrics()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showLyrics, closeLyrics])

  // Prevent body scroll when panel is open on mobile only
  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches
    if (showLyrics && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showLyrics])

  const lyrics = paper?.song?.lyrics || ''
  const title = suno?.title || paper?.song?.title || paper?.title
  const coverUrl = suno?.image_large_url || suno?.image_url
  const styleTags = suno?.display_tags || paper?.song?.style || ''
  const isLoading = !!currentPaperId && !paper
  const hasSong = !!currentPaperId

  // Don't render on song pages (they show lyrics inline) or about page
  if (isSongPage || isAboutPage) return null

  // Desktop: Always render the sidebar
  // Mobile: Only render when showLyrics is true
  return (
    <>
      {/* Mobile backdrop - only when showLyrics */}
      {showLyrics && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden animate-fade-in"
          onClick={closeLyrics}
        />
      )}

      {/* Mobile panel - only when showLyrics */}
      {showLyrics && (
        <div 
          className="fixed z-50 bg-surface-900 border-surface-700/50
            inset-x-0 bottom-0 top-16 rounded-t-2xl border-t
            lg:hidden
            animate-slide-up
            overflow-hidden flex flex-col"
        >
          <MobilePanelContent 
            hasSong={hasSong}
            isLoading={isLoading}
            title={title}
            coverUrl={coverUrl}
            styleTags={styleTags}
            lyrics={lyrics}
            sunoId={suno?.id}
            onClose={closeLyrics}
          />
        </div>
      )}

      {/* Desktop sidebar - always visible */}
      <aside 
        className="hidden lg:flex lg:flex-col lg:w-[340px] lg:flex-shrink-0 
          bg-surface-900 border-l border-surface-700/50 h-full"
      >
        {hasSong ? (
          <DesktopSidebarContent 
            isLoading={isLoading}
            title={title}
            coverUrl={coverUrl}
            styleTags={styleTags}
            lyrics={lyrics}
            sunoId={suno?.id}
          />
        ) : (
          <EmptyState />
        )}
      </aside>
    </>
  )
}

interface ContentProps {
  isLoading?: boolean
  title?: string
  coverUrl?: string
  styleTags?: string
  lyrics: string
  sunoId?: string
}

interface MobileContentProps extends ContentProps {
  hasSong: boolean
  onClose: () => void
}

function MobilePanelContent({ hasSong, isLoading, title, coverUrl, styleTags, lyrics, sunoId, onClose }: MobileContentProps) {
  if (!hasSong) {
    return (
      <>
        <div className="flex-shrink-0 px-5 py-4 border-b border-surface-700/50 flex items-center justify-between">
          <span className="text-surface-400">Lyrics</span>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <EmptyState />
      </>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="flex-shrink-0 px-5 py-4 border-b border-surface-700/50 flex items-start gap-4">
        <CoverImage coverUrl={coverUrl} title={title} isLoading={isLoading} />
        
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <>
              <div className="h-5 w-3/4 rounded shimmer" />
              <div className="h-4 w-1/2 rounded shimmer mt-2" />
            </>
          ) : (
            <>
              <h3 className="font-semibold text-surface-100 line-clamp-2 leading-snug">
                <LatexText>{title || 'Unknown'}</LatexText>
              </h3>
              {styleTags && (
                <p className="text-surface-400 text-sm mt-1 line-clamp-1">
                  {styleTags}
                </p>
              )}
            </>
          )}
        </div>

        <button
          onClick={onClose}
          className="flex-shrink-0 p-2 -mr-2 -mt-1 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 transition-colors"
          aria-label="Close lyrics"
        >
          <X size={20} />
        </button>
      </div>

      {/* Lyrics content */}
      <div className="flex-1 overflow-y-auto p-5">
        {isLoading ? (
          <div className="space-y-2">
            <div className="h-4 w-full rounded shimmer" />
            <div className="h-4 w-5/6 rounded shimmer" />
            <div className="h-4 w-4/6 rounded shimmer" />
          </div>
        ) : lyrics ? (
          <div className="text-surface-200 text-sm whitespace-pre-wrap leading-relaxed">
            {lyrics}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-surface-400">No lyrics available</p>
          </div>
        )}
      </div>

      {/* Suno link */}
      {sunoId && (
        <div className="flex-shrink-0 px-5 py-4 border-t border-surface-700/50">
          <a
            href={`https://suno.com/song/${sunoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-4 py-2.5 bg-surface-800 hover:bg-surface-700 rounded-lg text-surface-300 hover:text-surface-100 text-sm font-medium transition-colors"
          >
            Listen on Suno →
          </a>
        </div>
      )}
    </>
  )
}

function DesktopSidebarContent({ isLoading, title, coverUrl, styleTags, lyrics, sunoId }: ContentProps) {
  return (
    <>
      {/* Header - sticky */}
      <div className="flex-shrink-0 px-5 py-4 border-b border-surface-700/50 flex items-start gap-4">
        <CoverImage coverUrl={coverUrl} title={title} isLoading={isLoading} />
        
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <>
              <div className="h-5 w-3/4 rounded shimmer" />
              <div className="h-4 w-1/2 rounded shimmer mt-2" />
            </>
          ) : (
            <>
              <h3 className="font-semibold text-surface-100 line-clamp-2 leading-snug">
                <LatexText>{title || 'Unknown'}</LatexText>
              </h3>
              {styleTags && (
                <p className="text-surface-400 text-sm mt-1 line-clamp-1">
                  {styleTags}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lyrics content - scrollable */}
      <div className="flex-1 overflow-y-auto p-5">
        {isLoading ? (
          <div className="space-y-2">
            <div className="h-4 w-full rounded shimmer" />
            <div className="h-4 w-5/6 rounded shimmer" />
            <div className="h-4 w-4/6 rounded shimmer" />
          </div>
        ) : lyrics ? (
          <div className="text-surface-200 text-sm whitespace-pre-wrap leading-relaxed">
            {lyrics}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-surface-400">No lyrics available</p>
          </div>
        )}
      </div>

      {/* Suno link */}
      {sunoId && (
        <div className="flex-shrink-0 px-5 py-4 border-t border-surface-700/50">
          <a
            href={`https://suno.com/song/${sunoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-4 py-2.5 bg-surface-800 hover:bg-surface-700 rounded-lg text-surface-300 hover:text-surface-100 text-sm font-medium transition-colors"
          >
            Listen on Suno →
          </a>
        </div>
      )}
    </>
  )
}

function CoverImage({ coverUrl, title, isLoading }: { coverUrl?: string; title?: string; isLoading?: boolean }) {
  if (isLoading || !coverUrl) {
    return (
      <div className="w-12 h-12 rounded-lg shimmer flex items-center justify-center flex-shrink-0">
        <svg className="w-6 h-6 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
        </svg>
      </div>
    )
  }

  return (
    <img 
      src={coverUrl}
      alt={title}
      className="w-12 h-12 rounded-lg object-cover shadow-lg flex-shrink-0"
    />
  )
}

function EmptyState() {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-800 flex items-center justify-center">
          <svg className="w-8 h-8 text-surface-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
        </div>
        <p className="text-surface-400 text-sm">Select a song to see lyrics</p>
      </div>
    </div>
  )
}
